import React from "react";
import { useMutation } from "@apollo/client";
import { gql } from "@apollo/client";
import { Form } from "react-final-form";
import arrayMutators from "final-form-arrays";
import { FieldArray } from "react-final-form-arrays";
import { FormattedNumber } from "react-intl";
import { Price, Dialog } from "@syos-music/common";
import { QuantityField, TextField } from "@syos-music/form";

import {
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  IconButton,
  Grid,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Delete as DeleteIcon, Plus as AddIcon } from "mdi-material-ui";

import PriceField from "components/form/PriceField";

const MUTATION = gql`
  mutation (
    $invoiceId: ID!
    $rows: [CreditNoteRowInput]!
    $subTotal: PriceInput!
    $taxRate: Float!
    $taxTotal: PriceInput!
    $total: PriceInput!
  ) {
    CreateCreditNote(
      invoiceId: $invoiceId
      rows: $rows
      subTotal: $subTotal
      taxRate: $taxRate
      taxTotal: $taxTotal
      total: $total
    ) {
      creditNote {
        id
        invoice {
          id
          creditNotes {
            id
            reference
            createdAt
            file {
              id
              name
              url
            }
          }
        }
      }
    }
  }
`;

export default function CreateCreditNote({ invoice, children }) {
  const [isOpen, setIsOpen] = React.useState(false);

  const open = () => setIsOpen(true);
  const close = () => setIsOpen(false);

  const [createCreditNote] = useMutation(MUTATION);

  const initialValues = React.useMemo(() => {
    if (!invoice) return null;

    const result = {
      rows: invoice.rows.slice(0),
    };

    if (invoice.shippingFee > 0) {
      result.rows.push({
        description: {
          en: "Shipping fee",
          fr: "Frais de livraison",
        },
        unitPrice: invoice.shippingFee,
        quantity: 1,
        total: invoice.shippingFee,
        accountingCode: "708500",
      });
    }

    return result;
  }, [invoice]);

  if (!invoice) return null;

  return (
    <>
      {children({ isOpen, open, close })}

      <Dialog open={isOpen} onClose={close} maxWidth="md">
        <Form
          mutators={{ ...arrayMutators }}
          onSubmit={(values) => {
            const rows = values.rows.map((row) => ({
              ...row,
              total: {
                currency: row.unitPrice.currency,
                value: row.unitPrice.value * row.quantity,
              },
            }));

            const subTotal = {
              currency: invoice.subTotal.currency,
              value: Math.max(
                0,
                rows.reduce((acc, row) => acc + row.total.value, 0)
              ),
            };

            const taxRate = invoice.taxRate;
            const taxTotal = {
              currency: invoice.taxTotal.currency,
              value: subTotal.value * taxRate,
            };
            const total = {
              currency: invoice.total.currency,
              value: subTotal.value + taxTotal.value,
            };

            return createCreditNote({
              variables: {
                invoiceId: invoice.id,
                rows,
                subTotal,
                taxRate,
                taxTotal,
                total,
              },
            })
              .then(close)
              .catch(() => true);
          }}
          initialValues={initialValues}
          keepDirtyOnReinitialize
        >
          {({ handleSubmit, submitting, values }) => {
            const subTotal = {
              currency: invoice.subTotal.currency,
              value: Math.max(
                0,
                values.rows.reduce(
                  (acc, row) => acc + row.unitPrice.value * row.quantity,
                  0
                )
              ),
            };

            const taxRate = invoice.taxRate;
            const taxTotal = {
              currency: invoice.taxTotal.currency,
              value: subTotal.value * taxRate,
            };

            const total = {
              currency: invoice.total.currency,
              value: subTotal.value + taxTotal.value,
            };

            return (
              <form onSubmit={handleSubmit} noValidate autoComplete="off">
                <Dialog.Title color="primary">Créer un avoir</Dialog.Title>

                <Dialog.Content>
                  <TableContainer>
                    <Table
                      sx={{
                        "& th:nth-of-type(1)": { minWidth: 38 },
                        "& th:nth-of-type(2)": { width: "100%" },
                        "& th:nth-of-type(4)": { minWidth: 120 },

                        "& td": {
                          whiteSpace: "nowrap",
                          "&:nth-of-type(2)": { width: "50%" },
                        },
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox"></TableCell>
                          <TableCell>Produit</TableCell>
                          <TableCell align="center">TVA</TableCell>
                          <TableCell align="center">P.U. HT</TableCell>
                          <TableCell align="center">Qté</TableCell>
                          <TableCell align="center">Total HT</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <FieldArray name="rows">
                          {({ fields }) => (
                            <>
                              {fields.map((fieldName, idx) => {
                                return (
                                  <TableRow key={fieldName}>
                                    <TableCell padding="checkbox">
                                      <IconButton
                                        onClick={() => fields.remove(idx)}
                                        size="small"
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </TableCell>

                                    <TableCell>
                                      <Grid container spacing={1}>
                                        <Grid item xs={12}>
                                          <TextField
                                            name={`${fieldName}.description.${invoice.locale}`}
                                            required
                                          />
                                        </Grid>

                                        <Grid item xs>
                                          <TextField
                                            name={`${fieldName}.reference`}
                                            placeholder="Référence produit"
                                            size="small"
                                          />
                                        </Grid>

                                        <Grid item>
                                          <TextField
                                            name={`${fieldName}.accountingCode`}
                                            required
                                            placeholder="Code comptable"
                                            size="small"
                                            sx={{ width: 120 }}
                                          />
                                        </Grid>
                                      </Grid>
                                    </TableCell>

                                    <TableCell align="right">
                                      <FormattedNumber
                                        value={invoice.taxRate}
                                        // eslint-disable-next-line react/style-prop-object
                                        style="percent"
                                        minimumFractionDigits={1}
                                        maximumFractionDigits={1}
                                      />
                                    </TableCell>

                                    <TableCell align="right">
                                      <PriceField
                                        name={`${fieldName}.unitPrice`}
                                        required
                                        sx={{ width: 100 }}
                                      />
                                    </TableCell>

                                    <TableCell align="center">
                                      <QuantityField
                                        name={`${fieldName}.quantity`}
                                        fullWidth={false}
                                      />
                                    </TableCell>

                                    <TableCell align="right">
                                      <Price
                                        value={
                                          fields.value[idx].unitPrice.value *
                                          fields.value[idx].quantity
                                        }
                                        currency={
                                          fields.value[idx].unitPrice.currency
                                        }
                                      />
                                    </TableCell>
                                  </TableRow>
                                );
                              })}

                              <TableRow>
                                <TableCell colSpan={6} align="right">
                                  <Button
                                    onClick={() =>
                                      fields.push({
                                        unitPrice: {
                                          currency: invoice.total.currency,
                                        },
                                        quantity: 1,
                                      })
                                    }
                                    startIcon={<AddIcon />}
                                    size="small"
                                    variant="text"
                                  >
                                    Ajouter une ligne
                                  </Button>
                                </TableCell>
                              </TableRow>
                            </>
                          )}
                        </FieldArray>

                        <TableRow>
                          <TableCell
                            rowSpan={5}
                            colSpan={2}
                            sx={{ borderColor: "transparent" }}
                          />
                          <TableCell colSpan={3}>Sous-total</TableCell>
                          <TableCell align="right">
                            <Price {...subTotal} />
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell colSpan={3}>
                            TVA&nbsp;
                            <FormattedNumber
                              value={invoice.taxRate}
                              // eslint-disable-next-line react/style-prop-object
                              style="percent"
                              minimumFractionDigits={1}
                              maximumFractionDigits={1}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Price {...taxTotal} />
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell
                            colSpan={3}
                            sx={{ borderColor: "transparent" }}
                          >
                            Total
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ borderColor: "transparent" }}
                          >
                            <Price {...total} />
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Dialog.Content>

                <Dialog.Actions>
                  <Button variant="text" onClick={close}>
                    Annuler
                  </Button>

                  <LoadingButton type="submit" loading={submitting}>
                    Créer
                  </LoadingButton>
                </Dialog.Actions>
              </form>
            );
          }}
        </Form>
      </Dialog>
    </>
  );
}

CreateCreditNote.fragments = {
  invoice: gql`
    fragment Billing_CreateCreditNote_invoice on Invoice {
      id
      locale

      rows {
        description {
          en
          fr
        }
        reference
        unitPrice {
          currency
          value
        }
        quantity
        total {
          currency
          value
        }
        accountingCode
      }

      subTotal {
        currency
        value
      }
      shippingFee {
        currency
        value
      }
      taxRate
      taxTotal {
        currency
        value
      }
      total {
        currency
        value
      }
    }
  `,
};
